// Хранилище оценок (cp со стороны белых) + артефакты движка на узлах.
// EvalStore — явная зависимость вместо глобального evalCache v3.
import type { GameNode } from './types';
import type { EngineAnalysis } from './eval-utils';

export const MATE_CP = 90000;

export function evalKey(fen: string): string {
  const p = String(fen).trim().split(/\s+/);
  return `${p[0]} ${p[1] || 'w'} ${p[2] || '-'} ${p[3] || '-'}`;
}

export function nodeFen(n: Pick<GameNode, 'fen' | 'placement' | 'turn'>): string {
  return n.fen || `${n.placement} ${n.turn} - - 0 1`;
}

export function nodeEvalKey(n: Pick<GameNode, 'fen' | 'placement' | 'turn'>): string {
  return evalKey(nodeFen(n));
}

/** Оценки позиций партии/сессии. Живёт на уровне сессии. */
export class EvalStore {
  private map = new Map<string, number>();

  get(fenOrKey: string): number | null {
    const v = this.map.get(fenOrKey.includes('/') ? evalKey(fenOrKey) : fenOrKey);
    return v == null ? null : v;
  }

  getNode(n: Pick<GameNode, 'fen' | 'placement' | 'turn'>): number | null {
    return this.get(nodeEvalKey(n));
  }

  set(fen: string, cpWhite: number): void {
    this.map.set(evalKey(fen), cpWhite);
  }

  has(fen: string): boolean {
    return this.map.has(evalKey(fen));
  }

  /** Записать результат анализа движка (cp со стороны хода → со стороны белых). */
  cacheAnalysis(fen: string, analysis: Pick<EngineAnalysis, 'cp' | 'mate'>): void {
    const cpWhite = cpWhiteFromLine(fen, analysis);
    if (cpWhite != null) this.set(fen, cpWhite);
  }

  clear(): void {
    this.map.clear();
  }

  get size(): number {
    return this.map.size;
  }
}

interface LineLike {
  cp?: number | null;
  mate?: number | null;
}

/** cp линии движка со стороны белых; маты схлопываются в ±100000. */
export function cpWhiteFromLine(fen: string, line: LineLike | null | undefined): number | null {
  if (!line) return null;
  if (line.cp != null) return fen.split(' ')[1] === 'w' ? line.cp : -line.cp;
  if (line.mate != null) {
    const mateW = fen.split(' ')[1] === 'w' ? line.mate : -line.mate;
    return mateW > 0 ? 100000 : -100000;
  }
  return null;
}

export function mateWhiteFromLine(fen: string, line: LineLike | null | undefined): number | null {
  if (!line || line.mate == null) return null;
  return fen.split(' ')[1] === 'w' ? line.mate : -line.mate;
}

/** Записать артефакты анализа на узел (для классификации/great/brilliant). */
export function setEngineArtifacts(node: GameNode, analysis: EngineAnalysis, depth: number): void {
  const fen = nodeFen(node);
  const pv = analysis?.pv || analysis?.lines?.[0]?.pv || [];
  node._pv = Array.isArray(pv) ? pv : [];
  node._bestmove = analysis?.bestmove || node._pv[0] || null;
  node._depth = analysis?.depth || depth;
  node._bestCp = cpWhiteFromLine(fen, analysis?.lines?.[0] || analysis);
  node._secondCp = cpWhiteFromLine(fen, analysis?.lines?.[1] || null);
  node._mateWhite = mateWhiteFromLine(fen, analysis);
}

/** Артефакты валидны только если посчитаны на нужной глубине (S-12 из v3). */
export function engineArtifactsValid(node: GameNode | null | undefined, depth: number): boolean {
  return Number(node?._depth) >= depth;
}

/** cp со стороны ходившего в узел. */
export function moverCp(node: GameNode, cpWhite: number | null): number | null {
  const p = node?.parent;
  if (!p || cpWhite == null) return null;
  return p.turn === 'w' ? cpWhite : -cpWhite;
}

export function nodeMateWhite(node: GameNode | null, evals: EvalStore, depth: number): number | null {
  if (node && node._mateWhite != null && engineArtifactsValid(node, depth)) return node._mateWhite;
  const cp = node ? evals.getNode(node) : null;
  if (cp == null || Math.abs(cp) < MATE_CP) return null;
  return cp > 0 ? 99 : -99;
}

export function moverMate(node: GameNode, mateWhite: number | null): number | null {
  const p = node?.parent;
  if (!p || mateWhite == null) return null;
  return p.turn === 'w' ? mateWhite : -mateWhite;
}

/** Win% из centipawns (формула Lichess). */
export function winPct(cp: number): number {
  return 50 + 50 * (2 / (1 + Math.exp(-0.00368208 * cp)) - 1);
}
